import { Component, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { PageTitleService } from 'app/services/page-title/page-title.service';
import { Routenames } from '../route-names.enum';
import { AuthService } from '../services/auth/auth.service';
import { AuthorisationService } from '../services/auth/authorisation.service';
import { Features } from '../services/auth/features';
import { Functionalities } from '../services/auth/functionalities';

interface LoginCodeForm {
    email: FormControl<string | null>;
    code: FormControl<string | null>;
}

interface LoginErrorResponse {
    message?: string;
}

@Component({
    selector: 'app-login-code',
    templateUrl: './login-code.component.html',
    styleUrls: ['../login/login.component.scss'],
    standalone: false,
})
export class LoginCodeComponent implements OnInit {
    readonly fcCode = new FormControl<string | null>(null, [Validators.required, Validators.pattern(/[0-9]+/)]);
    readonly loginForm = new FormGroup<LoginCodeForm>({
        email: new FormControl<string | null>(null, [Validators.required, Validators.email]),
        code: this.fcCode,
    });

    errorMessage: string | null = null;
    validating = false;

    constructor(
        private authService: AuthService,
        private router: Router,
        private route: ActivatedRoute,
        private pageTitleService: PageTitleService
    ) {}

    ngOnInit(): void {
        this.pageTitleService.setTitle('Inloggen');
        this.loginForm.controls.email.setValue(this.route.snapshot.queryParamMap.get('email'));
    }

    pasteCode(event: ClipboardEvent): false {
        const pastedText = event.clipboardData?.getData('text').replace(/\D/g, '') ?? '';
        this.fcCode.setValue(pastedText);
        return false;
    }

    validateCode(): void {
        if (this.validating || this.loginForm.invalid) {
            return;
        }

        this.errorMessage = null;
        this.validating = true;
        this.authService.loginPin(this.loginForm.controls.email.value ?? '', this.fcCode.value ?? '').subscribe({
            next: () => {
                this.validating = false;
                if (AuthorisationService.hasFeature(Features.CustomersRecentSorted)) {
                    this.router.navigate([Routenames.customers], { replaceUrl: true });
                } else if (AuthorisationService.hasFunctionality(Functionalities.Timeline)) {
                    this.router.navigate([Routenames.timeline], { replaceUrl: true });
                } else {
                    this.router.navigate([Routenames.settings], { replaceUrl: true });
                }
            },
            error: (failReason: LoginErrorResponse) => {
                this.validating = false;
                this.errorMessage = failReason.message ?? 'Kon de server niet bereiken';
            },
        });
    }

    resendCode(): void {
        this.errorMessage = null;
        this.fcCode.reset();
        this.authService.loginEmail(this.loginForm.controls.email.value ?? '').subscribe({
            error: (failReason: LoginErrorResponse) => {
                this.errorMessage = failReason.message ?? 'Kon geen nieuwe code versturen';
            },
        });
    }
}
